import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPageContent, type PageSection } from '../../../utils/pageContentStorage';

const DEFAULT_IMAGE = 'https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/home-hero-bg.jpg';

export default function HeroSection() {
  const navigate = useNavigate();
  const [heroData, setHeroData] = useState<PageSection | null>(null);

  useEffect(() => {
    fetchPageContent().then(content => {
      const hero = content.find(s => s.id === 'home-hero');
      if (hero) setHeroData(hero);
    });
  }, []);

  const heroImage = heroData?.image || DEFAULT_IMAGE;
  const heroTitle = heroData?.title ?? '專業手機維修，值得信賴的選擇';
  const heroSubtitle = heroData?.subtitle ?? 'iPhone · iPad · MacBook · Android 全系列維修。使用原廠規格零件，多數維修當日完工，提供三個月保固。';

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="維修工作室"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950/90 via-gray-900/70 to-gray-900/40" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 text-orange-400 text-xs font-medium px-4 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse" />
            專業維修 · 快速取件 · 原廠零件
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {heroTitle}
          </h1>

          <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-lg">
            {heroSubtitle}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              立即查詢報價
            </button>
            <button
              onClick={() => navigate('/services')}
              className="px-8 py-3.5 border border-white/40 hover:border-white text-white font-semibold rounded-full transition-all duration-200 hover:bg-white/10 whitespace-nowrap cursor-pointer"
            >
              了解服務項目
            </button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center gap-6 mt-12">
            {[
              { icon: 'ri-shield-check-line', text: '3個月保固' },
              { icon: 'ri-time-line', text: '當日完工' },
              { icon: 'ri-award-line', text: '原廠規格零件' },
              { icon: 'ri-customer-service-2-line', text: '免費診斷' },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-2 text-gray-300 text-sm">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${b.icon} text-orange-400`} />
                </div>
                <span>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Stats Card */}
      <div className="absolute bottom-12 right-12 hidden xl:block">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-white">
          <div className="grid grid-cols-2 gap-6">
            {[
              { num: '5000+', label: '成功案例' },
              { num: '98%', label: '客戶滿意度' },
              { num: '1小時', label: '平均維修時間' },
              { num: '3個月', label: '維修保固' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl font-bold text-orange-400">{s.num}</div>
                <div className="text-xs text-gray-300 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
